/** Internal: virtual joysticks (touch, deadzone, normalized). Use InputManager only. */

import type {
    JoystickConfig,
    JoystickState,
    Vector2Like,
    MousePosition
} from "./Input_Types";

export class JoystickManager {
    private joysticks: Map<string, JoystickState> = new Map();
    private joystickConfigs: Map<string, Required<JoystickConfig>> = new Map();

    /** Add a virtual joystick. */
    addJoystick(config: JoystickConfig): void {
        // Fill in default values
        const fullConfig: Required<JoystickConfig> = {
            id: config.id,
            zone: config.zone,
            customPosition: config.customPosition ?? { x: 0.5, y: 0.5 },
            zoneRadius: config.zoneRadius ?? 100,
            maxDistance: config.maxDistance ?? 60,
            deadzone: config.deadzone ?? 0.1,
            showVisual: config.showVisual ?? true,
            autoReturn: config.autoReturn ?? true,
        };

        this.joystickConfigs.set(config.id, fullConfig);

        // Initialize joystick state
        const state: JoystickState = {
            id: config.id,
            isActive: false,
            vector: { x: 0, y: 0 },
            rawVector: { x: 0, y: 0 },
            distance: 0,
            angle: 0,
            touchId: null,
            centerPosition: this.getJoystickZoneCenter(fullConfig),
            currentPosition: { x: 0, y: 0 },
        };

        this.joysticks.set(config.id, state);
    }

    /** Remove joystick by id. */
    removeJoystick(id: string): void {
        this.joysticks.delete(id);
        this.joystickConfigs.delete(id);
    }

    /** Full joystick state by id, or null. */
    getJoystick(id: string): JoystickState | null {
        const state = this.joysticks.get(id);
        return state ? { ...state } : null;
    }

    /** Normalized vector (-1..1, deadzone applied). */
    getJoystickVector(id: string): Vector2Like {
        const state = this.joysticks.get(id);
        return state ? { ...state.vector } : { x: 0, y: 0 };
    }

    /** Joystick angle in radians (-π..π). */
    getJoystickAngle(id: string): number {
        const state = this.joysticks.get(id);
        return state ? state.angle : 0;
    }

    /** Normalized distance from center (0..1, deadzone applied). */
    getJoystickDistance(id: string): number {
        const state = this.joysticks.get(id);
        return state ? state.distance : 0;
    }

    /** True if joystick is currently touched. */
    isJoystickActive(id: string): boolean {
        const state = this.joysticks.get(id);
        return state ? state.isActive : false;
    }

    /** All registered joystick ids. */
    getJoystickIds(): string[] {
        return Array.from(this.joysticks.keys());
    }

    /** Reset joystick to center. */
    resetJoystick(id: string): void {
        const state = this.joysticks.get(id);
        if (!state) return;

        state.vector = { x: 0, y: 0 };
        state.rawVector = { x: 0, y: 0 };
        state.distance = 0;
        state.angle = 0;
        state.currentPosition = { ...state.centerPosition };
    }

    /** Clear all joysticks and reset to center. */
    clearAll(): void {
        for (const [id] of this.joysticks) {
            this.resetJoystick(id);
            const state = this.joysticks.get(id);
            if (state) {
                state.isActive = false;
                state.touchId = null;
            }
        }
    }

    /** Handle touch start; returns true if joystick claimed it. */
    handleTouchStart(touch: Touch, getTouchPosition: (touch: Touch) => MousePosition): boolean {
        const touchPos = getTouchPosition(touch).screen;
        const joystickId = this.getTouchJoystickZone(touchPos);

        if (joystickId) {
            const state = this.joysticks.get(joystickId);
            const config = this.joystickConfigs.get(joystickId);
            if (!state || !config) return false;

            // Only activate if joystick is not already active
            // This prevents resetting the joystick when multiple touches are processed
            if (!state.isActive || state.touchId === null) {
                state.isActive = true;
                state.touchId = touch.identifier;
                state.centerPosition = touchPos;  // Dynamic center where touch started
                state.currentPosition = touchPos;
            }

            return true; // Touch was handled by joystick
        }

        return false;
    }

    /** Handle touch move; returns true if joystick claimed it. */
    handleTouchMove(touch: Touch, getTouchPosition: (touch: Touch) => MousePosition): boolean {
        // Find joystick controlled by this touch
        for (const [id, state] of this.joysticks) {
            if (state.touchId === touch.identifier) {
                const touchPos = getTouchPosition(touch).screen;
                this.updateJoystickFromTouch(id, touchPos);
                return true;
            }
        }
        return false;
    }

    /** Handle touch end; returns true if joystick claimed it. */
    handleTouchEnd(touchId: number): boolean {
        for (const [id, state] of this.joysticks) {
            if (state.touchId === touchId) {
                const config = this.joystickConfigs.get(id);
                if (!config) continue;

                state.isActive = false;
                state.touchId = null;

                // Auto-return to center if enabled
                if (config.autoReturn) {
                    this.resetJoystick(id);
                }

                return true;
            }
        }
        return false;
    }

    /** True if touch id is claimed by a joystick. */
    isTouchUsedByJoystick(touchId: number): boolean {
        for (const state of this.joysticks.values()) {
            if (state.touchId === touchId) {
                return true;
            }
        }
        return false;
    }

    /** All joystick states (internal). */
    getAllStates(): Map<string, JoystickState> {
        return this.joysticks;
    }

    private getJoystickZoneCenter(config: Required<JoystickConfig>): Vector2Like {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const margin = config.zoneRadius;

        switch (config.zone) {
            case "bottom-left":
                return { x: margin, y: height - margin };
            case "bottom-right":
                return { x: width - margin, y: height - margin };
            case "top-left":
                return { x: margin, y: margin };
            case "top-right":
                return { x: width - margin, y: margin };
            case "center":
                return { x: width / 2, y: height / 2 };
            case "custom":
                return {
                    x: width * config.customPosition.x,
                    y: height * config.customPosition.y
                };
            default:
                return { x: margin, y: height - margin };
        }
    }

    private getTouchJoystickZone(touchPos: Vector2Like): string | null {
        for (const [id, config] of this.joystickConfigs) {
            const state = this.joysticks.get(id);
            if (!state) continue;

            const center = state.isActive ? state.centerPosition : this.getJoystickZoneCenter(config);
            const dx = touchPos.x - center.x;
            const dy = touchPos.y - center.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= config.zoneRadius) {
                return id;
            }
        }
        return null;
    }

    private updateJoystickFromTouch(id: string, touchPos: Vector2Like): void {
        const state = this.joysticks.get(id);
        const config = this.joystickConfigs.get(id);
        if (!state || !config) return;

        // Calculate raw offset from center
        const dx = touchPos.x - state.centerPosition.x;
        const dy = -(touchPos.y - state.centerPosition.y); // Invert Y: screen down = negative, screen up = positive
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Clamp to max distance
        const clampedDistance = Math.min(distance, config.maxDistance);
        const angle = Math.atan2(dy, dx);

        // Calculate raw vector (clamped)
        state.rawVector = {
            x: Math.cos(angle) * clampedDistance,
            y: Math.sin(angle) * clampedDistance
        };

        // Calculate normalized vector (-1 to 1)
        const normalizedDistance = clampedDistance / config.maxDistance;

        // Apply deadzone
        const deadzoneApplied = normalizedDistance < config.deadzone ? 0 :
            (normalizedDistance - config.deadzone) / (1 - config.deadzone);

        state.vector = {
            x: Math.cos(angle) * deadzoneApplied,
            y: Math.sin(angle) * deadzoneApplied
        };

        state.distance = deadzoneApplied;
        state.angle = angle;
        state.currentPosition = {
            x: state.centerPosition.x + state.rawVector.x,
            y: state.centerPosition.y - state.rawVector.y  // Invert Y for screen position
        };
    }
}

